import React, { useEffect } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * Shared chrome for every panel (Calendar, Expenses, Documents, Workspace).
 *
 * mode="split"     — column to the right of the chat (default)
 * mode="expanded"  — covers the chat area (conv sidebar + icon rail still visible)
 *
 * The chat itself stays mounted underneath in expanded mode, so toggling
 * back to split returns the user to the same scroll position and message
 * stream they left.
 *
 * ESC closes the panel.
 */
const PanelShell = ({
    title,
    icon = null,
    mode = 'split',
    onClose,
    onToggleExpand,
    headerExtras = null,
    children,
    className = '',
    // When true, the shared chrome header (icon + title + expand/close) is NOT
    // rendered — the child owns the entire surface and draws its own single-row
    // header (Kanban Total Redesign #112). ESC-to-close still works.
    noChromeHeader = false,
}) => {
    const { t } = useLanguage();
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape' && onClose) onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    return (
        // Opacity (#362, RE-OPENED) — `mode` deliberately does NOT vary the
        // panel's own surface class any more. The prior fix applied an extra
        // `md:pai-panel--expanded` modifier (a stronger backing alpha) only
        // when `mode === 'expanded'`, on the theory that a bigger panel needs
        // more compensation. That theory broke on the PO's own comparison: a
        // panel reaching the SAME full width by closing its sibling instead
        // of clicking the expand arrows renders at the identical rect and
        // reads correctly at the plain base alpha — so the size argument
        // never held, and the bump made the button-triggered state read as
        // MORE see-through on this dark wallpaper, not less (see the long
        // comment on `.pai-panel::before` in index.css for the measured
        // reasoning). Every mode now shares one class, `shell:pai-panel`, so an
        // expanded panel is byte-identical, CSS-wise, to the same panel
        // filling the same width any other way. File Manager is unaffected
        // either way — it is an opaque modal, never a `.pai-panel` occupant.
        <div className={`h-full flex flex-col overflow-hidden shell:pai-panel bg-pai-base shell:bg-transparent ${className}`}>
            {!noChromeHeader && (
                <div className="px-7 pt-3.5 pb-2.5 flex items-center justify-between gap-3 flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        {icon && <span className="text-white flex-shrink-0">{icon}</span>}
                        <h2 className="text-[27px] font-extrabold tracking-[-0.025em] text-white truncate">{title}</h2>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {headerExtras}
                        {onToggleExpand && (
                            <button
                                onClick={onToggleExpand}
                                className="pai-icon-btn w-[38px] h-[38px]"
                                title={mode === 'expanded' ? t('navCollapseToSplitView', 'Collapse to split view') : t('navExpandToCoverChat', 'Expand to cover chat')}
                            >
                                {mode === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                            </button>
                        )}
                        {onClose && (
                            <button
                                onClick={onClose}
                                className="pai-icon-btn w-[38px] h-[38px]"
                                title={t('navClosePanelEsc', 'Close panel (Esc)')}
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                </div>
            )}
            <div className={`flex-1 min-h-0 ${noChromeHeader ? 'flex flex-col' : 'overflow-y-auto'}`}>
                {children}
            </div>
        </div>
    );
};

export default PanelShell;
